import { useEffect, useState, useRef } from "react";
import { Session } from "@supabase/supabase-js";
import { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAuth = (queryClient: QueryClient) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    const getSession = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error fetching session:", error);
          if (mounted.current) {
            toast({
              title: "Authentication Error",
              description: "There was an error connecting to the authentication service.",
              variant: "destructive",
            });
          }
          return;
        }

        if (mounted.current) {
          setSession(currentSession);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error in getSession:", error);
        if (mounted.current) {
          setLoading(false);
        }
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      if (mounted.current) {
        setSession(currentSession);
        setLoading(false);
        if (_event === 'SIGNED_OUT') {
          queryClient.clear();
        }
      }
    });

    return () => {
      mounted.current = false;
      subscription.unsubscribe();
    };
  }, [queryClient, toast]);

  return { session, loading };
};